import { Table } from "@tanstack/react-table"
import {
    MySubscriptionsTableDataI,
    mySubscriptionsWellNameSep,
} from "../tableTypes"
import { FunctionComponent, useEffect, useMemo, useState } from "react"
import { useBreakPointContext } from "../../../../../../context/breakPointContext"
import {
    faChevronDoubleDown,
    faChevronDoubleUp,
} from "@fortawesome/pro-solid-svg-icons"
import styles from "./actionHeader.module.scss"
import { METRIC_COLUMN_ID, SUBSCRIBED_WELLS_COLUMN_ID } from "../column"
import Button from "../../../../../shared/button"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Input from "../../../../../shared/input"
import { sortArray } from "../../../../../../util/arrays/array"
import { useNavigate } from "react-router-dom"
import { NOTIFICATIONS_SUBSCRIPTION_ADD_SUBSCRIPTION_CHILD } from "../../../../../.."

interface MySubscriptionsActionHeaderI {
    table: Table<MySubscriptionsTableDataI>
    globalFilter: string
}

const MySubscriptionsActionHeader: FunctionComponent<
    MySubscriptionsActionHeaderI
> = ({ table, globalFilter }) => {
    const navigate = useNavigate()

    const breakPoint = useBreakPointContext()
    const is_LE_mediumBreakPoint = breakPoint === "sm" || breakPoint === "md"

    // state
    const [showActions, setShowActions] = useState(true)

    // font awesome icon
    const fontAwesomeIcon = showActions
        ? faChevronDoubleUp
        : faChevronDoubleDown

    // styling classes
    // in mobile
    const outerClass =
        is_LE_mediumBreakPoint && !showActions
            ? styles.outerClosed
            : styles.outer

    const metricColumn = table.getColumn(METRIC_COLUMN_ID)
    const subscribedWellsColumn = table.getColumn(SUBSCRIBED_WELLS_COLUMN_ID)

    // unique metrics
    const m = metricColumn?.getFacetedUniqueValues().keys() ?? []
    const uniqueMetrics = Array.from(m).sort()

    // unique wells
    const uniqueWells = useMemo(() => {
        const wellKeys =
            subscribedWellsColumn?.getFacetedUniqueValues().keys() ?? []

        const arrayOfWells = Array.from(wellKeys)

        const uniqueWells = Array.from(
            new Set(
                arrayOfWells.flatMap((well) => {
                    return well.split(mySubscriptionsWellNameSep)
                })
            )
        )

        return sortArray(uniqueWells) as string[]
    }, [table])

    const metricFilterValue = (metricColumn?.getFilterValue() as string) || ""
    const wellFilterValue =
        (subscribedWellsColumn?.getFilterValue() as string) || ""

    // useEffect: if we resize from mobile => desktop sizing we need to setShowActions to true
    useEffect(() => {
        if (!is_LE_mediumBreakPoint) {
            setShowActions(true)
        }
    }, [is_LE_mediumBreakPoint])

    return (
        <div className={outerClass}>
            {is_LE_mediumBreakPoint && (
                <Button
                    handleButtonClick={() => setShowActions(!showActions)}
                    status="primary"
                    customButtonClassName={styles.mobileButton}
                >
                    <FontAwesomeIcon icon={fontAwesomeIcon} color="#fff" />
                </Button>
            )}
            {showActions && (
                <div className={styles.container}>
                    <div className={styles.containerLeft}>
                        <div className={styles.addSubscriptionBtn}>
                            <Button
                                handleButtonClick={() => {
                                    navigate(
                                        NOTIFICATIONS_SUBSCRIPTION_ADD_SUBSCRIPTION_CHILD
                                    )
                                }}
                                status="primary"
                                condition="default"
                            >
                                Add Subscription
                            </Button>
                        </div>
                        <div className={styles.metricFilter}>
                            <Input
                                onChange={(e) => {
                                    metricColumn?.setFilterValue(e.target.value)
                                }}
                                orientation="horizontal"
                                type="datalist"
                                placeHolder="Filter By Metric"
                                datalistId="alertRuleMetric"
                                options={uniqueMetrics}
                                specializedClass="noMessage"
                                value={metricFilterValue}
                            />
                        </div>
                        <div className={styles.metricFilter}>
                            <Input
                                onChange={(e) => {
                                    subscribedWellsColumn?.setFilterValue(
                                        e.target.value
                                    )
                                }}
                                orientation="horizontal"
                                type="datalist"
                                placeHolder="Filter By Well"
                                datalistId="wellDatalist"
                                options={uniqueWells}
                                specializedClass="noMessage"
                                value={wellFilterValue}
                            />
                        </div>
                    </div>
                    <div className={styles.containerRight}>
                        <div className={styles.globalFilter}>
                            <Input
                                value={globalFilter}
                                onChange={(e) => {
                                    table.setGlobalFilter(e.target.value)
                                }}
                                type="text"
                                orientation="horizontal"
                                placeHolder="Search Columns"
                                specializedClass="noMessage"
                                inputCustomClass={styles.searchFilter}
                            />
                        </div>
                        <Button
                            handleButtonClick={() => {
                                table.setGlobalFilter("")
                            }}
                            status="secondary"
                            condition="default"
                        >
                            Clear
                        </Button>
                    </div>
                </div>
            )}
        </div>
    )
}

export default MySubscriptionsActionHeader
